import { Chart } from 'react-chartjs-2';
import type { ChartOptions } from 'chart.js';
import { useTranslation } from 'react-i18next';

interface LineChartProps {
  data: {
    media_ciclo: number[];
    control_sd_l: number[];
    control_sd_u: number[];
    lowerBound: number[];
    upperBound: number[];
    significativas: number[];
  };
  title: string;
  setLoading: (loading: boolean) => void;
  selectedVar: string; // Para determinar los colores
}

const color = (selectedVar: string) => {
  switch (selectedVar) {
    case 'hip_l':
      return 'red';
    case 'hip_r':
      return 'blue';
    case 'knee_l':
      return 'green';
    default:
      return 'orange';
  }
};

const color_sd = (selectedVar: string) => {
  switch (selectedVar) {
    case 'hip_l':
      return 'rgba(255, 107, 107, 0.4)';
    case 'hip_r':
      return 'rgba(107, 184, 255, 0.4)';
    case 'knee_l':
      return 'rgba(46, 255, 27, 0.4)';
    default:
      return 'rgba(255, 161, 55, 0.4)';
  }
};
const verticalPlugin = {
  //linea vertical en el 60 cambio a swing
  id: 'verticalLine',
  beforeDraw: (chart) => {
    const xValue = 60;
    const xScale = chart.scales.x;
    const yScale = chart.scales.y;
    const ctx = chart.ctx;

    const x = xScale.getPixelForValue(xValue);

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(x, yScale.top);
    ctx.lineTo(x, yScale.bottom);
    ctx.strokeStyle = 'red';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.restore();
  },
};

function reorderData(data) {
  //para que la función empiece en el stance
  const firstPart = data.slice(25); // Toma los datos del índice 25 al final
  const secondPart = data.slice(0, 25); // Toma los primeros 25 datos
  return [...firstPart, ...secondPart]; // Junta ambas partes
}

export function LineChart(props: Readonly<LineChartProps>) {
  const { t } = useTranslation();
  const { data, title, selectedVar } = props;

  const chartData = {
    labels: Array.from({ length: 100 }, (_, i) => i),
    datasets: [
      // {
      //   label: 'Diferencia significativa',
      //   data: reorderData(data.significativas),
      //   backgroundColor: 'rgba(255, 0, 0, 0.1)',
      //   fill: true,
      //   pointRadius: 0,
      //   borderWidth: 0,
      //   spanGaps: false,
      //   datalabels: { display: false },
      // },

      {
        label: t('pages.patients.viewPatient.sessions.mean'),
        data: reorderData(data.media_ciclo),
        borderColor: color(selectedVar),
        fill: false,
        pointRadius: 0,
        borderWidth: 1,
        datalabels: { display: false },
      },
      {
        label: '',
        data: reorderData(data.upperBound),
        backgroundColor: color_sd(selectedVar),
        pointRadius: 0,
        borderWidth: 0,
        datalabels: { display: false },
      },
      {
        label: t('pages.patients.viewPatient.sessions.sd'),
        data: reorderData(data.lowerBound),
        fill: '-1',
        backgroundColor: color_sd(selectedVar),
        pointRadius: 0,
        borderWidth: 0,
        datalabels: { display: false },
      },

      {
        label: '',
        data: reorderData(data.control_sd_l),
        backgroundColor: 'rgba(204, 204, 204, 0.5)',
        pointRadius: 0,
        borderWidth: 0,
        datalabels: { display: false },
      },
      {
        label: t('pages.patients.viewPatient.sessions.control'),
        data: reorderData(data.control_sd_u),
        fill: '-1',
        backgroundColor: 'rgba(199, 199, 199, 0.5)',
        pointRadius: 0,
        borderWidth: 0,
        datalabels: { display: false },
      },
    ],
  };

  const defaultOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          filter: (item) => {
            // Filtrar la leyenda para que no muestre las líneas de control y SD
            return !['', ''].includes(item.text);
          },
        },
      },
      title: {
        display: true,
        text: title,
        font: { size: 18 },
      },
    },
    scales: {
      x: {
        ticks: { stepSize: 10 },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.gait_cycle'),
          font: { size: 15, weight: 'bold' },
        },
      },
      y: {
        ticks: { stepSize: 10 },
        title: {
          display: true,
          text: t('pages.patients.viewPatient.sessions.joint_ang'),
          font: { size: 15, weight: 'bold' },
        },
      },
    },
  };

  return (
    <div>
      {data && (
        <div style={{ position: 'relative', width: '100%', height: '500px' }}>
          <Chart
            type="line"
            data={chartData}
            options={defaultOptions}
            plugins={[verticalPlugin]}
          />
        </div>
      )}
    </div>
  );
}

export default LineChart;
